import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Camera, Package, User, ArrowRight, Home, Sparkles } from 'lucide-react';

/**
 * Welcome page for first-time users after registration/email confirmation
 * Shows a friendly welcome message and prompts to book their first shoot
 */
export default function Welcome() {
  const { user } = useAuth();
  
  // Get first name for personalized greeting
  const firstName = user?.full_name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen bg-[#050816]">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12 md:py-20">
        {/* Hero Welcome */}
        <div className="text-center mb-12">
          <div className="mx-auto w-20 h-20 bg-[#C6A14A]/20 rounded-full flex items-center justify-center mb-6">
            <Sparkles className="h-10 w-10 text-[#C6A14A]" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-light text-white mb-4">
            Welcome to RC Studios, <span className="text-[#C6A14A]">{firstName}</span>!
          </h1>
          
          <p className="text-[#E8E4DE]/60 text-lg max-w-xl mx-auto">
            Your account is all set up. Let's capture something beautiful together.
          </p>
        </div>

        {/* Main CTA - Book a Shoot */}
        <Card className="bg-[#0f1724] border-[#C6A14A]/30 mb-8" data-testid="book-shoot-card">
          <CardContent className="p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
                  <Camera className="h-8 w-8 text-[#C6A14A]" />
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-xl md:text-2xl font-medium text-white mb-2">
                  Ready to Book a Shoot?
                </h2>
                <p className="text-[#E8E4DE]/60 mb-4">
                  Professional real estate photography starting at $199. Fast turnaround, stunning results.
                </p>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] px-8"
                  data-testid="welcome-book-shoot-btn"
                >
                  <Link to="/book">
                    Book a Shoot
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Secondary Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-[#0f1724] border-white/10 hover:border-[#C6A14A]/30 transition-colors">
            <Link to="/orders" className="block p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#C6A14A]/10 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-[#C6A14A]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">My Orders</h3>
                  <p className="text-[#E8E4DE]/50 text-sm">View order history</p>
                </div>
              </div>
            </Link>
          </Card>

          <Card className="bg-[#0f1724] border-white/10 hover:border-[#C6A14A]/30 transition-colors">
            <Link to="/account" className="block p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#C6A14A]/10 rounded-lg flex items-center justify-center">
                  <User className="h-5 w-5 text-[#C6A14A]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">My Account</h3>
                  <p className="text-[#E8E4DE]/50 text-sm">Update your profile</p>
                </div>
              </div>
            </Link>
          </Card>

          <Card className="bg-[#0f1724] border-white/10 hover:border-[#C6A14A]/30 transition-colors">
            <Link to="/pricing/real-estate" className="block p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#C6A14A]/10 rounded-lg flex items-center justify-center">
                  <Home className="h-5 w-5 text-[#C6A14A]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">View Pricing</h3>
                  <p className="text-[#E8E4DE]/50 text-sm">See all packages</p>
                </div>
              </div>
            </Link>
          </Card>
        </div>

        {/* Help text */}
        <div className="text-center mt-12">
          <p className="text-[#E8E4DE]/40 text-sm">
            Questions? Contact us at{' '}
            <a href="mailto:hello@rcstudios.net" className="text-[#C6A14A] hover:underline">
              hello@rcstudios.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
